import deliveryAlarm from "../assets/audio/delivery-alarm.wav";
import deliveryAfterAlarm from "../assets/audio/delivery.wav";
import logo from "../assets/logo.png";

// =============================
// GLOBAL STATE (ANTI DUPLICATE FIX)
// =============================
let alarmTimers = new Map(); // ✅ key-based timers
let firedAlarms = new Set();
let pollingInterval = null;

export let isDeliveryBlocked = false;
export const blockedOrderIds = new Set();

// =============================
// EVENT SYSTEM
// =============================
const emitEvent = (name, detail) => {
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

// =============================
// UNBLOCK
// =============================
export const unblockDelivery = (orderId) => {
  blockedOrderIds.delete(String(orderId));

  emitEvent("deliveryBlockUpdated", {
    type: "unblock",
    orderId,
    blockedOrderIds: Array.from(blockedOrderIds),
  });

  if (blockedOrderIds.size === 0) {
    isDeliveryBlocked = false;
    emitEvent("deliveryBlockCleared", {});
  }
};

// =============================
// TIME PARSER
// =============================
const parseSlotEndTime = (expectedDelivery, slotTiming) => {
  const base = new Date(expectedDelivery);
  if (!slotTiming) return base;

  const parts = slotTiming.split("-");
  if (parts.length < 2) return base;

  const endTime = parts[1].trim();
  const match = endTime.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
  if (!match) return base;

  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3].toUpperCase();

  if (meridiem === "PM" && hours !== 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;

  base.setHours(hours, minutes, 0, 0);
  return base;
};

// =============================
// ICON
// =============================
const getResizedIcon = (src, size = 64) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, size, size);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = src;
  });
};

// =============================
// BEFORE ALARM
// =============================
const fireDeliveryAlarm = async (order) => {
  const orderId = String(order?.order_id);

  if (firedAlarms.has(`before-${orderId}`)) return; // ✅ STOP DUPLICATE
  firedAlarms.add(`before-${orderId}`);

  const iconUrl = await getResizedIcon(logo, 64);

  emitEvent("deliveryAlarmBefore", {
    orderId,
    customerName: order?.customer_details?.customer_name || "",
  });

  if (Notification.permission === "granted") {
    new Notification("🚚 Delivery in 30 min!", {
      body: `Order #${orderId}`,
      icon: iconUrl,
      requireInteraction: true,
    });
  }

  new Audio(deliveryAlarm).play().catch(() => {});
};

// =============================
// AFTER ALARM (BLOCK)
// =============================
const fireAfterDeliveryAlarm = async (order) => {
  const orderId = String(order?.order_id);

  if (firedAlarms.has(`after-${orderId}`)) return; // ✅ STOP DUPLICATE
  firedAlarms.add(`after-${orderId}`);

  isDeliveryBlocked = true;
  blockedOrderIds.add(orderId);

  emitEvent("deliveryBlockActivated", {
    orderId,
    customerName: order?.customer_details?.customer_name || "",
    blockedOrderIds: Array.from(blockedOrderIds),
  });

  const iconUrl = await getResizedIcon(logo, 64);

  if (Notification.permission === "granted") {
    const n = new Notification("⚠️ Mark as Delivered!", {
      body: `Order #${orderId} overdue`,
      icon: iconUrl,
      requireInteraction: true,
    });

    // optional navigation
    n.onclick = () => {
      window.focus();
      window.location.href = `/order-list/${orderId}/ongoing`;
    };
  }

  new Audio(deliveryAfterAlarm).play().catch(() => {});
};

// =============================
// SCHEDULER (FIXED)
// =============================
export const scheduleDeliveryAlarmsGlobal = (orders) => {
  // clear old timers
  alarmTimers.forEach((t) => clearTimeout(t));
  alarmTimers.clear?.();
  alarmTimers = new Map();

  const now = Date.now();
  const THIRTY_MIN = 30 * 60 * 1000;
  const TEN_MIN_AFTER = 10 * 60 * 1000;

  orders.forEach((order) => {
    if (["Delivered", "Cancelled", "Failed"].includes(order?.order_status))
      return;

    const orderId = String(order?.order_id);

    const slotEnd = parseSlotEndTime(
      order.expected_delivery,
      order.slot_timing
    ).getTime();

    // ================= BEFORE =================
    const beforeKey = `before-${orderId}`;
    const beforeDelay = slotEnd - THIRTY_MIN - now;

    if (beforeDelay > 0 && !firedAlarms.has(beforeKey)) {
      const t = setTimeout(() => fireDeliveryAlarm(order), beforeDelay);
      alarmTimers.set(beforeKey, t);
    }

    // ================= AFTER =================
    const afterKey = `after-${orderId}`;
    const afterDelay = slotEnd + TEN_MIN_AFTER - now;

    if (afterDelay > 0 && !firedAlarms.has(afterKey)) {
      const t = setTimeout(() => fireAfterDeliveryAlarm(order), afterDelay);
      alarmTimers.set(afterKey, t);
    }
  });
};

// =============================
// POLLING (SAFE)
// =============================
export const startAlarmPolling = (fetchFn) => {
  fetchFn();

  if (pollingInterval) clearInterval(pollingInterval);

  pollingInterval = setInterval(() => {
    fetchFn();
  }, 5 * 60 * 1000);
};

export const stopAlarmPolling = () => {
  if (pollingInterval) clearInterval(pollingInterval);
};